import { useMutation, useQuery } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import Autorenew from '@mui/icons-material/Autorenew'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Create'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { DateTime } from 'luxon'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ControlledSelect } from 'src/components/Inputs/Controlled/Select'
import { StatefulButton } from 'src/components/StatefulButton'
import { graphql } from 'src/gql'
import { TigersMemberShowQuery } from 'src/gql/graphql'
import * as yup from 'yup'

interface Props {
  member: TigersMemberShowQuery['findMember']
}

interface EligibilityInfoFormValues {
  firstName: string
  lastName: string
  birthDate: DateTime
  insuranceMemberId?: string
  healthPlanId?: number
  stateId?: number
}

const MIN_BIRTHDAY = DateTime.local().minus({ years: 13 }).startOf('day')

const HealthPlansQuery = graphql(`
  query HealthPlans {
    supportedHealthPlans {
      id
      name
    }
  }
`)

const InsuranceStatesQuery = graphql(`
  query InsuranceStates {
    insuranceStates {
      id
      name
    }
  }
`)

const Mutation = graphql(`
  mutation UpdateEligibilityInfo($input: UpdateEligibilityInput!) {
    updateEligibilityInfo(input: $input) {
      success
      message
      member {
        ... on Member {
          id
          name
          firstName
          lastName
          birthDate
          eligibilityStatus
          healthPlanInfo {
            id
            insuranceId
            healthPlan {
              id
              name
            }
            state {
              id
              name
            }
          }
        }
      }
    }
  }
`)

export const UpdateEligibilityInfo = ({ member }: Props) => {
  const [isEditing, setEditing] = useState<boolean>(false)
  const { enqueueSnackbar } = useSnackbar()

  const [update, { loading }] = useMutation(Mutation, {
    onCompleted: ({ updateEligibilityInfo: { success, message } }) => {
      enqueueSnackbar(message, { variant: success ? 'success' : 'error' })
      resetForm()
    },
    onError: () => {
      enqueueSnackbar('Oops. Something went wrong.', { variant: 'error' })
    },
    refetchQueries: ['TigersMemberShow'],
  })

  const { data: healthPlans } = useQuery(HealthPlansQuery, {
    fetchPolicy: 'network-only',
  })
  const healthPlanOptions = healthPlans?.supportedHealthPlans.map(
    ({ id, name }) => {
      return { value: id, label: name, key: name }
    },
  )

  const { data: states } = useQuery(InsuranceStatesQuery, {
    fetchPolicy: 'network-only',
  })

  const statesOptions = states?.insuranceStates.map(({ id, name }) => {
    return { value: id, label: name, key: name }
  })

  const defaultValues = {
    firstName: member?.firstName ?? undefined,
    lastName: member?.lastName ?? undefined,
    birthDate: member?.birthDate?.toUTC(),
    insuranceMemberId: member?.healthPlanInfo?.insuranceId,
    healthPlanId: member?.healthPlanInfo?.healthPlan.id,
    stateId: member?.healthPlanInfo?.state.id,
  }

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EligibilityInfoFormValues>({
    defaultValues,
    resolver: yupResolver(schema),
  })

  if (!member) return null

  const onSubmit = async ({
    insuranceMemberId,
    healthPlanId,
    stateId,
    ...input
  }: EligibilityInfoFormValues) => {
    const insurance =
      insuranceMemberId && healthPlanId && stateId
        ? { insuranceMemberId, healthPlanId, stateId }
        : null

    return update({
      variables: { input: { ...input, insurance, memberId: member.id } },
    })
  }

  const resetForm = () => {
    reset(defaultValues)
    setEditing(false)
  }

  const handleEdit = () => {
    reset(defaultValues)
    setEditing(true)
  }

  const checkEligibility = () => {
    reset(defaultValues)
    handleSubmit(onSubmit)()
  }

  return (
    <>
      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            flexDirection="column"
            marginY={t => t.spacing(2)}
            sx={t => ({
              '& > *:not(:last-child)': { mb: t.spacing(2) },
            })}>
            <Box display="flex" flexDirection="row">
              <FormControl
                fullWidth
                sx={t => ({
                  mr: t.spacing(2),
                })}>
                <TextField
                  {...register('firstName')}
                  label="First Name"
                  variant="outlined"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  {...register('lastName')}
                  label="Last Name"
                  variant="outlined"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              </FormControl>
            </Box>
            <Box>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="birthDate"
                  render={({ field: { value, onChange } }) => (
                    <DatePicker
                      value={value}
                      onChange={date => onChange(date)}
                      disableFuture
                      openTo="year"
                      views={['year', 'month', 'day']}
                      format="MM/dd/yyyy"
                      label="DOB"
                      slotProps={{
                        textField: {
                          error: !!errors.birthDate,
                          helperText: errors.birthDate?.message,
                          value,
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Box>
            <Box>
              <FormControl fullWidth>
                <TextField
                  {...register('insuranceMemberId', {
                    setValueAs: v => (v === '' ? undefined : v),
                  })}
                  label="Member Insurance ID"
                  variant="outlined"
                  error={!!errors.insuranceMemberId}
                  helperText={errors.insuranceMemberId?.message}
                />
              </FormControl>
            </Box>
            <Box>
              {statesOptions && (
                <ControlledSelect
                  control={control}
                  name="stateId"
                  label="State"
                  options={statesOptions}
                  fullWidth
                  error={errors.stateId}
                />
              )}
            </Box>
            <Box display="flex" flexDirection="row">
              {healthPlanOptions && (
                <ControlledSelect
                  control={control}
                  name="healthPlanId"
                  label="Health Plan"
                  options={healthPlanOptions}
                  fullWidth
                  error={errors.healthPlanId}
                />
              )}
              <Box display="flex" alignItems="center">
                <IconButton
                  type="submit"
                  disabled={isSubmitting}
                  title="Confirm"
                  size="small">
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CheckIcon htmlColor={'green'} />
                  )}
                </IconButton>
                <IconButton title="Reset" onClick={resetForm} size="small">
                  <CloseIcon htmlColor="red" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </form>
      ) : (
        <Box
          marginY={t => t.spacing(2)}
          display="flex"
          gap={2}
          justifyContent="flex-end">
          {member.healthPlanInfo && (
            <StatefulButton
              size="small"
              color="secondary"
              endIcon={<Autorenew />}
              onClick={checkEligibility}
              loading={loading}
              variant="outlined">
              Check Eligibility
            </StatefulButton>
          )}
          <Button
            size="small"
            endIcon={<EditIcon />}
            onClick={handleEdit}
            variant="outlined">
            Edit Eligibility
          </Button>
        </Box>
      )}
      <Divider />
    </>
  )
}

const schema = yup.object().shape({
  firstName: yup.string().required('Required').trim(),
  lastName: yup.string().required('Required').trim(),
  birthDate: yup
    .mixed<DateTime>()
    .test(value => (value ? value < MIN_BIRTHDAY : true))
    .required('Required'),
  insuranceMemberId: yup
    .string()
    .nullable()
    .matches(
      new RegExp('^[a-zA-Z0-9-]{6,15}$'),
      'Must include between 6 and 15 alphanumeric characters',
    ),
  healthPlanId: yup.number().when('insuranceMemberId', {
    is: (insuranceMemberId: string) => !!insuranceMemberId,
    then: schema =>
      schema.required('Health plan required when Insurance ID is set'),
    otherwise: schema => schema.nullable(),
  }),
  stateId: yup.number().when('insuranceMemberId', {
    is: (insuranceMemberId: string) => !!insuranceMemberId,
    then: schema => schema.required('State required when Insurance ID is set'),
    otherwise: schema => schema.nullable(),
  }),
})
